import CakeIcon from '@mui/icons-material/Cake';
import { getBackendUrl } from '../config';
import { CalendarEvent } from '../types';
import {
    parseRolloverPracticeEvent,
    convertTournamentGameToCalendarEvent,
    mergeTournamentAndCalendarEvents
} from './eventUtils';
import { getHolidayEvents } from '../components/holidayUtils';
import { getTournamentCalendarEvents, TOURNAMENTS } from '../tournaments';
import { getCachedTournamentData } from './tournamentScraper';
import { authenticatedFetch, fetchUnifiedCalendarEvents } from './apiClient';

export interface FetchCalendarOptions {
    forceRefresh?: boolean;
    keepMultiDayPlaceholder?: boolean;
    includeBirthdays?: boolean;
    includeHolidays?: boolean;
    seasonKey?: string;
}

/**
 * Fetches calendar events and volunteer duties from the backend and merges them.
 * Queries the canonical `/api/calendar/unified` backend endpoint first, and transparently
 * falls back to legacy `/api/calendar` + `/api/volunteer-duties` client merging if unavailable.
 * @returns A promise that resolves to an array of CalendarEvent objects, with duties merged in.
 */
export async function fetchAndMergeCalendarData(options?: FetchCalendarOptions): Promise<CalendarEvent[]> {
    const keepPlaceholders = options?.keepMultiDayPlaceholder ?? true;

    if (typeof fetchUnifiedCalendarEvents === 'function') {
        try {
            const unified = await fetchUnifiedCalendarEvents({
                seasonKey: options?.seasonKey,
                keepPlaceholders,
                includeBirthdays: options?.includeBirthdays ?? true,
                includeHolidays: options?.includeHolidays ?? true,
                forceRefresh: options?.forceRefresh ?? false,
            });
            if (Array.isArray(unified)) {
                const holidayLookup = new Map(
                    getHolidayEvents().map(h => [`${(h.summary || '').trim().toLowerCase()}-${(h.start || '').slice(0, 10)}`, h])
                );
                let hydratedEvents: CalendarEvent[] = unified.map(ev => {
                    if (ev.isHoliday) {
                        const match = holidayLookup.get(`${(ev.summary || '').trim().toLowerCase()}-${(ev.start || '').slice(0, 10)}`);
                        if (match) {
                            return { ...ev, icon: match.icon, color: ev.color || match.color };
                        }
                    }
                    if (ev.isBirthday && !ev.icon) {
                        return { ...ev, icon: CakeIcon, color: ev.color || '#ec4899' };
                    }
                    return ev;
                });

                // Idempotently merge any browser-cached tournament games
                for (const t of TOURNAMENTS) {
                    const cached = getCachedTournamentData(t.key);
                    if (cached && cached.games && cached.games.length > 0) {
                        const jetsGames = cached.games.filter(g =>
                            (g.homeTeam || '').toLowerCase().includes('jets') ||
                            (g.visitorTeam || '').toLowerCase().includes('jets')
                        );
                        if (jetsGames.length > 0) {
                            const jetsCalEvents = jetsGames.map(g => convertTournamentGameToCalendarEvent(g, t));
                            hydratedEvents = mergeTournamentAndCalendarEvents(hydratedEvents, jetsCalEvents, t, { keepMultiDayPlaceholder: keepPlaceholders });
                        }
                    }
                }

                return hydratedEvents;
            }
        } catch (err) {
            console.warn('Unified calendar endpoint unavailable, falling back to legacy calendar merge:', err);
        }
    }

    const [calResponse, dutiesResponse] = await Promise.all([
        authenticatedFetch(`${getBackendUrl()}/api/calendar`),
        authenticatedFetch(`${getBackendUrl()}/api/volunteer-duties`)
    ]);

    if (!calResponse.ok) {
        throw new Error(`Calendar fetch failed: ${calResponse.status}`);
    }
    const regularEvents: CalendarEvent[] = await calResponse.json();
    const holidayEvents = getHolidayEvents();
    const tournamentEvents = getTournamentCalendarEvents();

    let dutiesData: Record<string, any> = {};
    if (dutiesResponse.ok) {
        dutiesData = await dutiesResponse.json();
    } else {
        // It's not critical if duties fail, so we just warn.
        console.warn(`Could not fetch volunteer duties: ${dutiesResponse.status}`);
    }

    // Merge tournament events if not already present in regularEvents
    const mergedRegularAndTournaments = [...regularEvents];
    for (const tEvent of tournamentEvents) {
        const tStart = tEvent.start.substring(0, 10);
        const tEnd = tEvent.end.substring(0, 10);
        const tName = tEvent.summary.toLowerCase().trim();

        // Only match if an existing event is already a multi-day event spanning this tournament's date range
        const existingFullEvent = mergedRegularAndTournaments.find(e => {
            const eName = (e.summary || '').toLowerCase().trim();
            const eStart = (e.start || '').substring(0, 10);
            const eEnd = (e.end || '').substring(0, 10);
            return (eName === tName || eName.includes(tName)) && eStart === tStart && eEnd === tEnd;
        });

        if (existingFullEvent) {
            existingFullEvent.url = existingFullEvent.url || tEvent.url;
            existingFullEvent.isTournament = true;
        } else {
            mergedRegularAndTournaments.push(tEvent);
        }
    }

    // Combine regular events, tournament events, and holiday events, then sort by start date
    const calendarEvents = [...mergedRegularAndTournaments, ...holidayEvents].sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());

    // Merge duties into calendar events
    const dutiesMap = new Map(Object.entries(dutiesData));
    const thirtyMinutesInMs = 30 * 60 * 1000;

    const mergedEvents = calendarEvents.map(event => {
        const eventDate = new Date(event.start);

        // Find a matching duty within a +/- 30 minute window.
        for (const [dutyKey, dutyDetails] of Array.from(dutiesMap.entries())) {
            // The dutyKey is a full ISO string from the backend (e.g., '2025-09-05T23:00:00+00:00').
            const dutyDate = new Date(dutyKey);
            if (Math.abs(eventDate.getTime() - dutyDate.getTime()) <= thirtyMinutesInMs) {
                dutiesMap.delete(dutyKey); // Prevent re-matching

                let homeTeam = event.homeTeam;
                let awayTeam = event.awayTeam;
                const cleanDuties: Record<string, string> = {};

                if (dutyDetails && typeof dutyDetails === 'object') {
                    for (const [key, value] of Object.entries(dutyDetails)) {
                        const normKey = key.trim().toLowerCase();
                        if ((normKey === 'home team' || normKey === 'home') && typeof value === 'string' && value.trim()) {
                            homeTeam = homeTeam || value.trim();
                        } else if ((normKey === 'away team' || normKey === 'away' || normKey === 'visitor' || normKey === 'visitor team') && typeof value === 'string' && value.trim()) {
                            awayTeam = awayTeam || value.trim();
                        } else if (value && typeof value === 'string' && value.trim()) {
                            cleanDuties[key.trim()] = value.trim();
                        }
                    }
                }

                // Apply rollover practice parsing after finding a match
                const eventWithDuties: CalendarEvent = {
                    ...event,
                    homeTeam,
                    awayTeam,
                    duties: Object.keys(cleanDuties).length > 0 ? cleanDuties : undefined,
                };
                return parseRolloverPracticeEvent(eventWithDuties);
            }
        }
        return parseRolloverPracticeEvent(event);
    });

    // Merge calendar events with any available cached tournament games
    let enrichedEvents = mergedEvents;
    for (const t of TOURNAMENTS) {
        const cached = getCachedTournamentData(t.key);
        if (cached && cached.games && cached.games.length > 0) {
            const jetsGames = cached.games.filter(g =>
                (g.homeTeam || '').toLowerCase().includes('jets') ||
                (g.visitorTeam || '').toLowerCase().includes('jets')
            );
            if (jetsGames.length > 0) {
                const jetsCalEvents = jetsGames.map(g => convertTournamentGameToCalendarEvent(g, t));
                enrichedEvents = mergeTournamentAndCalendarEvents(enrichedEvents, jetsCalEvents, t, { keepMultiDayPlaceholder: true });
            }
        }
    }

    return enrichedEvents;
}